import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  Typography,
  Drawer,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Modal,
  message,
  Popconfirm,
  Select,
  Divider,
} from 'antd';
import { PlusOutlined, TrophyOutlined, SettingOutlined, StopOutlined, CheckCircleOutlined, BarChartOutlined, ContactsOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { engagementAdminService } from '../../services/engagementAdmin.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Aberto', color: 'green' },
  VALIDATING: { label: 'Validando', color: 'orange' },
  CLOSED: { label: 'Encerrado', color: 'default' },
};

export const EngagementCycleManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCycleKey, setSelectedCycleKey] = useState<string | null>(null);
  const [prizesModalOpen, setPrizesModalOpen] = useState(false);
  const [processWinnersModalOpen, setProcessWinnersModalOpen] = useState(false);
  const [winnersContactModalOpen, setWinnersContactModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [prizesForm] = Form.useForm();
  const [processForm] = Form.useForm();

  const { data: cyclesData, isLoading } = useQuery({
    queryKey: ['admin', 'engagement-cycles', statusFilter, page],
    queryFn: () => engagementAdminService.listCycles({ status: statusFilter, page, limit: 20 }),
  });

  const { data: selectedCycle } = useQuery({
    queryKey: ['admin', 'engagement-cycle', selectedCycleKey],
    queryFn: () => engagementAdminService.getCycleDetail(selectedCycleKey!),
    enabled: !!selectedCycleKey,
  });

  const { data: winnersContact, isLoading: isLoadingWinnersContact } = useQuery({
    queryKey: ['admin', 'engagement-cycle-winners-contact', selectedCycleKey],
    queryFn: () => engagementAdminService.getWinnersContactDetails(selectedCycleKey!),
    enabled: winnersContactModalOpen && !!selectedCycleKey,
  });

  const createMutation = useMutation({
    mutationFn: (values: any) =>
      engagementAdminService.createCycle({
        ...values,
        startsAt: values.startsAt?.toISOString(),
        endsAt: values.endsAt?.toISOString(),
      }),
    onSuccess: () => {
      message.success('Ciclo criado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycles'] });
      setDrawerOpen(false);
      form.resetFields();
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao criar ciclo'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ cycleKey, values }: any) =>
      engagementAdminService.updateCycle(cycleKey, {
        ...values,
        startsAt: values.startsAt?.toISOString(),
        endsAt: values.endsAt?.toISOString(),
        lotteryDrawDate: values.lotteryDrawDate?.toISOString(),
      }),
    onSuccess: (data) => {
      message.success('Ciclo atualizado');
      if (data?.cycleKey && data.cycleKey !== selectedCycleKey) {
        setSelectedCycleKey(data.cycleKey);
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycles'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycle'] });
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao atualizar ciclo'),
  });

  const closeMutation = useMutation({
    mutationFn: (cycleKey: string) => engagementAdminService.closeCycle(cycleKey, { reason: 'ADMIN' }),
    onSuccess: () => {
      message.success('Ciclo encerrado');
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycles'] });
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao encerrar ciclo'),
  });

  const setPrizesMutation = useMutation({
    mutationFn: ({ cycleKey, prizes }: any) => engagementAdminService.setPrizes(cycleKey, prizes),
    onSuccess: () => {
      message.success('Prêmios configurados');
      setPrizesModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycle'] });
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao configurar prêmios'),
  });

  const processWinnersMutation = useMutation({
    mutationFn: ({ cycleKey, drawnNumbers }: any) =>
      engagementAdminService.processWinners(cycleKey, drawnNumbers),
    onSuccess: () => {
      message.success('Ganhadores processados com sucesso');
      setProcessWinnersModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycle'] });
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao processar ganhadores'),
  });

  const inactivateMutation = useMutation({
    mutationFn: (cycleKey: string) => engagementAdminService.inactivateCycle(cycleKey),
    onSuccess: () => {
      message.success('Ciclo inativado');
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycles'] });
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao inativar ciclo'),
  });

  const reactivateMutation = useMutation({
    mutationFn: (cycleKey: string) => engagementAdminService.reactivateCycle(cycleKey),
    onSuccess: () => {
      message.success('Ciclo reativado');
      queryClient.invalidateQueries({ queryKey: ['admin', 'engagement-cycles'] });
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Erro ao reativar ciclo'),
  });

  const columns = [
    {
      title: 'Ciclo',
      dataIndex: 'cycleKey',
      key: 'cycleKey',
      render: (key: string) => <Text strong>{key}</Text>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => {
        const s = STATUS_LABELS[record.status] ?? { label: record.status, color: 'default' };
        return (
          <Space size={4}>
            <Tag color={s.color}>{s.label}</Tag>
            {record.inactivatedAt && <Tag color="red">Inativo</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Início',
      dataIndex: 'startsAt',
      key: 'startsAt',
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Fim',
      dataIndex: 'endsAt',
      key: 'endsAt',
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    {
      title: 'Prêmios',
      key: 'prizes',
      render: (_: any, record: any) => (
        <Text>{record.cyclePrizes?.length ?? 0} configurado(s)</Text>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            size="small"
            icon={<BarChartOutlined />}
            onClick={() => navigate(`/admin/engagement/cycles/${record.cycleKey}/report`)}
          >
            Relatório
          </Button>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => {
              setSelectedCycleKey(record.cycleKey);
              setDrawerOpen(true);
            }}
          >
            Editar
          </Button>
          <Button
            size="small"
            icon={<TrophyOutlined />}
            onClick={() => {
              setSelectedCycleKey(record.cycleKey);
              const initialValues: Record<string, string | undefined> = {};
              for (let i = 1; i <= 5; i++) {
                const prize = record.cyclePrizes?.find((p: any) => p.prizeRank === i);
                initialValues[`prizeValue${i}`] = prize?.prizeValue ?? undefined;
              }
              prizesForm.setFieldsValue(initialValues);
              setPrizesModalOpen(true);
            }}
          >
            Prêmios
          </Button>
          {record.status === 'CLOSED' && (
            <Button
              size="small"
              type="primary"
              onClick={() => {
                setSelectedCycleKey(record.cycleKey);
                setProcessWinnersModalOpen(true);
              }}
            >
              Sorteio
            </Button>
          )}
          {record.status === 'CLOSED' && (
            <Button
              size="small"
              icon={<ContactsOutlined />}
              onClick={() => {
                setSelectedCycleKey(record.cycleKey);
                setWinnersContactModalOpen(true);
              }}
            >
              Ganhadores
            </Button>
          )}
          {record.status === 'OPEN' && (
            <Popconfirm
              title="Encerrar ciclo antecipadamente?"
              onConfirm={() => closeMutation.mutate(record.cycleKey)}
            >
              <Button size="small" danger>
                Encerrar
              </Button>
            </Popconfirm>
          )}
          {!record.inactivatedAt ? (
            <Popconfirm
              title="Inativar ciclo?"
              description="O ciclo ficará oculto na página de engajamento dos participantes."
              onConfirm={() => inactivateMutation.mutate(record.cycleKey)}
              okText="Inativar"
              cancelText="Cancelar"
            >
              <Button size="small" icon={<StopOutlined />} danger>
                Inativar
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Reativar ciclo?"
              description="O ciclo voltará a aparecer na página de engajamento dos participantes."
              onConfirm={() => reactivateMutation.mutate(record.cycleKey)}
              okText="Reativar"
              cancelText="Cancelar"
            >
              <Button size="small" icon={<CheckCircleOutlined />}>
                Reativar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleCreateOrUpdate = async (values: any) => {
    if (selectedCycleKey) {
      updateMutation.mutate({ cycleKey: selectedCycleKey, values });
    } else {
      createMutation.mutate(values);
    }
  };

  const handleOpenCreate = () => {
    setSelectedCycleKey(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  useEffect(() => {
    if (!drawerOpen || !selectedCycleKey || !selectedCycle) {
      return;
    }

    form.setFieldsValue({
      newCycleKey: selectedCycle.cycleKey,
      startsAt: selectedCycle.startsAt ? dayjs(selectedCycle.startsAt) : undefined,
      endsAt: selectedCycle.endsAt ? dayjs(selectedCycle.endsAt) : undefined,
      surveyPoints: selectedCycle.surveyPoints,
      referralPoints: selectedCycle.referralPoints,
      minimumPrizePoints: selectedCycle.minimumPrizePoints,
      lotteryDrawNumber: selectedCycle.lotteryDrawNumber,
      lotteryDrawDate: selectedCycle.lotteryDrawDate ? dayjs(selectedCycle.lotteryDrawDate) : undefined,
      spaRegistrationNumber: selectedCycle.spaRegistrationNumber,
    });
  }, [drawerOpen, selectedCycleKey, selectedCycle, form]);

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={3} style={{ margin: 0 }}>
          Ciclos de Engajamento
        </Title>
        <Space>
          <Select
            placeholder="Filtrar por status"
            allowClear
            style={{ width: 160 }}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          >
            <Option value="OPEN">Aberto</Option>
            <Option value="VALIDATING">Validando</Option>
            <Option value="CLOSED">Encerrado</Option>
          </Select>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Novo Ciclo
          </Button>
        </Space>
      </Space>

      <Table
        columns={columns}
        dataSource={cyclesData?.cycles ?? []}
        rowKey="cycleKey"
        loading={isLoading}
        pagination={{
          current: page,
          total: cyclesData?.total ?? 0,
          pageSize: 20,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
      />

      {/* Create / Edit Drawer */}
      <Drawer
        title={selectedCycleKey ? `Editar ciclo ${selectedCycleKey}` : 'Novo ciclo'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCycleKey(null);
          form.resetFields();
        }}
        width={480}
        extra={
          <Button type="primary" onClick={() => form.submit()}>
            Salvar
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdate}
        >
          {!selectedCycleKey && (
            <Form.Item name="cycleKey" label="Chave do ciclo (YYYY-MM)" rules={[{ required: true, pattern: /^\d{4}-\d{2}$/ }]}>
              <Input placeholder="2025-06" />
            </Form.Item>
          )}
          {selectedCycleKey && (
            <Form.Item
              name="newCycleKey"
              label="Chave do ciclo"
              rules={[{ required: true, pattern: /^\d{4}-\d{2}[a-z]?$/, message: 'Formato inválido. Use YYYY-MM ou YYYY-MMx (ex: 2026-05b)' }]}
            >
              <Input placeholder="2026-05" />
            </Form.Item>
          )}
          <Form.Item name="startsAt" label="Início" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="endsAt" label="Fim" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Divider>Pontuação</Divider>
          <Form.Item name="surveyPoints" label="Pontos por pesquisa">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="referralPoints" label="Pontos por indicação">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="minimumPrizePoints" label="Pontos mínimos para prêmio">
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Divider>Loteria Federal</Divider>
          <Form.Item name="lotteryDrawNumber" label="Nº do concurso">
            <Input placeholder="0000" />
          </Form.Item>
          <Form.Item name="lotteryDrawDate" label="Data do sorteio">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="spaRegistrationNumber" label="Nº de registro SPA">
            <Input />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Prizes Modal */}
      <Modal
        title={`Configurar prêmios — ${selectedCycleKey}`}
        open={prizesModalOpen}
        onCancel={() => { setPrizesModalOpen(false); prizesForm.resetFields(); }}
        onOk={() => prizesForm.submit()}
        confirmLoading={setPrizesMutation.isPending}
      >
        <Form
          form={prizesForm}
          layout="vertical"
          onFinish={(values) => {
            const prizes = Array.from({ length: 5 }, (_, i) => ({
              prizeRank: i + 1,
              prizeValue: (values[`prizeValue${i + 1}`] ?? '').trim(),
            })).filter((p) => p.prizeValue.length > 0);
            setPrizesMutation.mutate({ cycleKey: selectedCycleKey, prizes });
          }}
        >
          {[1, 2, 3, 4, 5].map((rank) => (
            <Form.Item key={rank} name={`prizeValue${rank}`} label={`${rank}º Prêmio`}>
              <Input maxLength={150} showCount placeholder="Ex.: Vale-presente R$ 150,00 - Loja X" />
            </Form.Item>
          ))}
        </Form>
      </Modal>

      {/* Process Winners Modal */}
      <Modal
        title={`Processar sorteio — ${selectedCycleKey}`}
        open={processWinnersModalOpen}
        onCancel={() => setProcessWinnersModalOpen(false)}
        onOk={() => processForm.submit()}
        confirmLoading={processWinnersMutation.isPending}
      >
        <Form
          form={processForm}
          layout="vertical"
          onFinish={(values) => {
            const drawnNumbers = Object.values(values).filter(Boolean) as string[];
            processWinnersMutation.mutate({ cycleKey: selectedCycleKey, drawnNumbers });
          }}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Informe os números extraídos da Loteria Federal (sufixos de 5 dígitos), um por prêmio.
          </Text>
          {[1, 2, 3, 4, 5].map((rank) => (
            <Form.Item key={rank} name={`drawn${rank}`} label={`Número do ${rank}º prêmio`}>
              <Input maxLength={5} placeholder="00000" style={{ width: 120 }} />
            </Form.Item>
          ))}
        </Form>
      </Modal>

      {/* Winners Contact Details Modal */}
      <Modal
        title={`Ganhadores — ${selectedCycleKey}`}
        open={winnersContactModalOpen}
        onCancel={() => setWinnersContactModalOpen(false)}
        footer={null}
        width={800}
      >
        <Table
          size="small"
          rowKey="prizeRank"
          loading={isLoadingWinnersContact}
          dataSource={winnersContact ?? []}
          pagination={false}
          columns={[
            { title: 'Prêmio', dataIndex: 'prizeRank', key: 'prizeRank', render: (rank: number) => `${rank}º` },
            { title: 'Descrição', dataIndex: 'prizeValue', key: 'prizeValue' },
            { title: 'Nº sorteado', dataIndex: 'drawnNumber', key: 'drawnNumber', render: (v: string | null) => v ?? '-' },
            { title: 'Bilhete premiado', dataIndex: 'winningTicketNumber', key: 'winningTicketNumber', render: (v: string | null) => v ?? '-' },
            {
              title: 'Nome',
              dataIndex: 'name',
              key: 'name',
              render: (name: string | null, record: any) =>
                record.hasWinner ? name : <Tag color="default">Sem ganhador</Tag>,
            },
            { title: 'E-mail', dataIndex: 'email', key: 'email', render: (v: string | null) => v ?? '-' },
            { title: 'Telefone', dataIndex: 'phone', key: 'phone', render: (v: string | null) => v ?? '-' },
          ]}
        />
      </Modal>
    </div>
  );
};

export default EngagementCycleManagementPage;
